import { useQuery } from "@tanstack/react-query";
import { analyzeThemeColor, resolveSecondaryColor, SECONDARY_FOREGROUND } from "@tripbox/ui";
import { Alert, ConfigProvider, Spin } from "antd";
import axios from "axios";
import { useEffect, type ReactNode } from "react";
import { PORTAL_DEFAULT_PRIMARY, PORTAL_DEFAULT_SECONDARY } from "../lib/theme";
import { fetchTenantContext, TenantContext } from "./TenantContext";

/** Resolves tenant branding before rendering tenant-dependent routes. */
export const TenantProvider = ({ children }: { children: ReactNode }) => {
  const query = useQuery({
    queryKey: ["tenant-context", window.location.hostname],
    queryFn: fetchTenantContext,
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });

  useEffect(() => {
    const analysis = analyzeThemeColor(query.data?.branding?.primaryColor || PORTAL_DEFAULT_PRIMARY);
    const primaryColor = analysis.isAppropriate ? analysis.color! : PORTAL_DEFAULT_PRIMARY;
    const resolvedAnalysis = analysis.isAppropriate ? analysis : analyzeThemeColor(PORTAL_DEFAULT_PRIMARY);
    document.documentElement.style.setProperty("--brand-primary", primaryColor);
    document.documentElement.style.setProperty("--brand-primary-foreground", resolvedAnalysis.preferredForeground);
    return () => {
      document.documentElement.style.removeProperty("--brand-primary");
      document.documentElement.style.removeProperty("--brand-primary-foreground");
    };
  }, [query.data?.branding?.primaryColor]);

  useEffect(() => {
    const secondary = resolveSecondaryColor(query.data?.branding?.secondaryColor, PORTAL_DEFAULT_SECONDARY);
    document.documentElement.style.setProperty("--brand-secondary", secondary.color);
    document.documentElement.style.setProperty("--brand-secondary-hover", secondary.hover);
    return () => {
      document.documentElement.style.removeProperty("--brand-secondary");
      document.documentElement.style.removeProperty("--brand-secondary-hover");
    };
  }, [query.data?.branding?.secondaryColor]);

  if (query.isPending) {
    return (
      <main className="flex min-h-[100dvh] items-center justify-center bg-[#FBFBFB]" aria-label="Loading portal">
        <Spin size="large" />
      </main>
    );
  }

  if (query.isError || !query.data) {
    const message = axios.isAxiosError<{ error?: string }>(query.error)
      ? query.error.response?.data?.error
      : null;
    return (
      <main className="flex min-h-[100dvh] items-center justify-center bg-[#FBFBFB] p-6">
        <Alert
          className="w-full max-w-lg"
          type="error"
          showIcon
          message="Partner portal unavailable"
          description={message || "This domain is not connected to an active agent portal."}
        />
      </main>
    );
  }

  const requestedPrimaryColor = query.data.branding?.primaryColor || PORTAL_DEFAULT_PRIMARY;
  const primaryColorAnalysis = analyzeThemeColor(requestedPrimaryColor);
  const primaryColor = primaryColorAnalysis.isAppropriate
    ? primaryColorAnalysis.color!
    : PORTAL_DEFAULT_PRIMARY;
  const secondaryColor = resolveSecondaryColor(query.data.branding?.secondaryColor, PORTAL_DEFAULT_SECONDARY).color;
  return (
    <TenantContext.Provider value={query.data}>
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: secondaryColor,
            colorInfo: primaryColor,
            colorLink: primaryColor,
            colorTextLightSolid: SECONDARY_FOREGROUND,
          },
        }}
      >
        {children}
      </ConfigProvider>
    </TenantContext.Provider>
  );
};
